// Catalog.ID Client — the universal unlock redirect
// ============================================================================
// ONE lock gate for every cross-module handler family (cid-encrypted-storage
// M2c, §10 item 5, ruling 2026-09-17):
//
//   **Any request to CID that arrives while the key bag is locked raises the
//   CID tab and waits for the unlock, regardless of the sender's
//   `requiresUserInteraction` flag.**
//
// What the flag means after M2c is one thing only: "focus the CID tab BEFORE
// delivery, because this flow shows a dialog even when the bag is unlocked"
// (consent modals, OIDC consent, the ceremony prompts). It is no longer the
// difference between waiting and failing — a background caller that met a
// locked bag used to get an instant `cid-locked` nobody could act on, because
// nothing had raised the tab the member would have had to unlock.
//
// The mechanism is `focus-request {requestId}`, a hub CONTROL message (no
// `to`, no canonical bytes, no signature — see DashboardWebSocketHub
// `HandleFocusRequestAsync`). The hub honours it only from the connection the
// pending request is addressed to, and only once per request; the answer then
// navigates the member back to the panel they were on.
//
// NOTE: the tab's own socket is not the only sender. The CID browser socket
// only exists after `initShell`, i.e. after login/unlock completes, so a
// request that arrives while the member is still on the login screen never
// reaches this file at all — the cld-id relay sends the focus-request for it
// (`KeyGrantRelay.HandleDashboardMessageAsync`). This gate covers the narrower
// case of a live tab whose bag is locked. The hub de-duplicates the pair.

import { keys } from '../state/keys';

/**
 * How long a handler waits for the member to unlock before refusing
 * `cid-locked`. THE ONLY COPY — every family reads it through
 * {@link ensureUnlocked}.
 *
 * Five minutes: the wait now starts by raising the tab, so it is bounded by a
 * member walking to the screen and typing a PIN, not by a background poll's
 * patience. Two other budgets are pinned to it and must stay ordered:
 * the relay's post-focus tab wait is the same five minutes (`KeyGrantRelay`),
 * and every module's CID-bound request timeout is LONGER
 * (`CidRequestBudget.Timeout`, 6 min) so the module never reports `timeout`
 * while the CID login is still on screen.
 */
export const UNLOCK_WAIT_TIMEOUT_MS = 300_000;

/** The envelope fields the gate reads. Every request family satisfies this. */
export interface UnlockGateRequest {
    readonly requestId: string;
    readonly requiresUserInteraction?: boolean;
}

/**
 * The seams the gate takes from a family's dispatch deps. All optional: a
 * family that injects nothing gets the in-memory key bag and the socket
 * registered by `key-grant/client.ts`.
 */
export interface UnlockGateDeps {
    /**
     * Whether the thing this handler needs is available now. Defaults to the
     * key bag being loaded. The blob family overrides it with "the member has
     * a live HTTP session", which is what its modes actually spend.
     */
    isUnlocked?: () => boolean;
    /** Resolves when the bag is next loaded. Defaults to {@link keys.waitForLoad}. */
    waitForUnlock?: () => Promise<void>;
    /** Ask the hub to raise this tab for `requestId`. Defaults to the live socket. */
    requestFocus?: (requestId: string) => void;
    /** Test seam; production uses {@link UNLOCK_WAIT_TIMEOUT_MS}. */
    unlockWaitTimeoutMs?: number;
}

/**
 * The page's live hub socket, registered by `key-grant/client.ts` when it
 * connects. Module-level for the same reason `keys` is: there is exactly one
 * per page, and threading it through thirteen dispatch-deps interfaces would
 * buy nothing but thirteen chances to forget it.
 */
let focusSender: ((requestId: string) => void) | null = null;

/** Install (or, with `null`, drop) the outbound `focus-request` sender. */
export function setFocusRequestSender(send: ((requestId: string) => void) | null): void {
    focusSender = send;
}

/**
 * Ask the hub to raise this tab for `requestId` — the same `focus-request`
 * the lock gate sends, for a handler that is about to show a dialog the
 * sender did not ask to focus for. `send` overrides the page's socket (tests).
 * A send failure is swallowed, as in {@link ensureUnlocked}.
 */
export function requestTabFocus(requestId: string, send?: (requestId: string) => void): void {
    const requestFocus = send ?? focusSender;
    if (!requestFocus) return;
    try {
        requestFocus(requestId);
    } catch {
        /* transport-level; the dialog still opens */
    }
}

/**
 * Serve-or-refuse gate: `true` when the handler may proceed, `false` when the
 * member never unlocked within the budget and the caller must answer
 * `cid-locked`.
 *
 * Unlocked ⇒ returns `true` immediately and sends NOTHING, so a request that
 * can be served silently never touches the member's screen. Locked ⇒ sends
 * exactly ONE `focus-request` for this request id (whatever the flag says; the
 * hub ignores the duplicate when the flag already raised the tab) and then
 * races the unlock against the budget.
 *
 * A send failure is swallowed: a dead socket means the tab is going away and
 * the relay's own focus-request is the one that will land — refusing here
 * would turn a transport hiccup into a user-visible error.
 */
export async function ensureUnlocked(
    request: UnlockGateRequest,
    deps: UnlockGateDeps = {},
): Promise<boolean> {
    const isUnlocked = deps.isUnlocked ?? (() => keys.isLoaded());
    if (isUnlocked()) return true;

    const requestFocus = deps.requestFocus ?? focusSender;
    if (requestFocus) {
        try {
            requestFocus(request.requestId);
        } catch {
            /* transport-level; the wait below is still worth taking */
        }
    }

    const waitForUnlock = deps.waitForUnlock ?? (() => keys.waitForLoad());
    const budget = deps.unlockWaitTimeoutMs ?? UNLOCK_WAIT_TIMEOUT_MS;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const arrived = await Promise.race([
        waitForUnlock().then(() => true),
        new Promise<boolean>(resolve => { timer = setTimeout(() => resolve(false), budget); }),
    ]);
    if (timer !== undefined) clearTimeout(timer);
    if (!arrived) return false;

    // Re-check rather than trust the wait: `waitForLoad` reports that the bag
    // was loaded, which is not the same claim as the caller's own condition
    // (the blob family wants a session), and a bag can be cleared again
    // between the resolve and here.
    return isUnlocked();
}
